import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import moment from 'moment';
import { combineLatest, ReplaySubject } from 'rxjs';
import { takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EditGrid, ResponseStatus } from '../../../shared';
import { Message, SelectItem } from '../../../shared/efaact-primeng.module';
import { UnsavedChangesService } from '../../../shared/modal-dialog';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { IUserInfo } from '../../../user-info';
import { IndirectRateStructure } from '../../store/indirect-rate-structure';

@Component({
    selector: 'ef-indirect-rate-structure',
    templateUrl: './indirect-rate-structure.component.html',
    styleUrls: ['./indirect-rate-structure.component.scss'],
    standalone: false
})
export class IndirectRateStructureComponent
    extends EditGrid<IndirectRateStructure>
    implements OnInit, OnDestroy, OnChanges
{
    rowTrackBy = (_index: number, item: IndirectRateStructure) => item?.id;

    @Input() userInfo: Partial<IUserInfo>;
    @Input() rates: SelectItem[];
    @Input() showAdjustments: boolean;
    @Input() FiscalYearEndDate: Date;
    @Input() disabled: boolean;
    private ratesSubject = new ReplaySubject<SelectItem[]>(1);
    public filteredRates: SelectItem[];
    private itemsSubject = new ReplaySubject<IndirectRateStructure[]>(1);
    public editing: IndirectRateStructure = null as IndirectRateStructure;
    selected: IndirectRateStructure[];
    cloned: { [i: number]: IndirectRateStructure } = {};
    dropdownItemHeight = environment.dropdownItemHeight;
    moment: any;
    messages: Message[];
    fyimpactEnabled = false;
    private active = true;
    constructor(
        public confirmservice: UnsavedChangesService,
        private log: LoggingService
    ) {
        super();
        this.moment = moment;
    }
    private addTemplate: any = {
        id: 'add',
        Adjusteddate: null,
        Adjustedrate: null,
        Calcshortdesc: '',
        Calctype: '',
        Costcenterid: -1,
        FYadjusted: null,
        FYenddate: null,
        Maintdate: null,
        Maintdate_pending: null,
        Modifiedby: null,
        Ratepercent_pending: null,
        Ratepercent: null,
        Row_Version: '0x0000000000000000',
        Status: {
            Message: '',
            MsgEntityID: null,
            MsgProcessID: null,
            StatusCode: 0
        }
    };
    ngOnInit() {
        this.setupSubscriptions();
    }
    ngOnDestroy() {
        this.active = false;
    }
    ngOnChanges(changes: SimpleChanges) {
        this.log.verbose({
            type: 'IndirectRateStructureComponent:ngOnChanges',
            changes
        });
        if (changes.items && changes.items.currentValue) {
            this.itemsSubject.next(changes.items.currentValue);
            this.fyimpactEnabled = false;
        }
        if (changes.rates && changes.rates.currentValue) {
            this.ratesSubject.next(changes.rates.currentValue);
        }
    }
    setupSubscriptions() {
        combineLatest([this.ratesSubject, this.itemsSubject])
            .pipe(
                takeWhile(() => this.active),
                tap(([rates, items]) =>
                    this.log.verbose({
                        type: 'IndirectRateStructureComponent:setupSubscriptions',
                        rates,
                        items
                    })
                )
            )
            .subscribe(([rates, items]) => {
                this.filteredRates = rates.filter(
                    x =>
                        !(
                            (x.value === 'MHR' || x.value === 'M2R') &&
                            items.some(y => y.Calctype === x.value)
                        )
                );
            });

        this.SaveItem.pipe(takeWhile(() => this.active)).subscribe(() => {
            this.fyimpactEnabled = true;
        });
    }
    onRowEditSave(item: IndirectRateStructure) {
        // do not call super, this has a confirm popup
        // super.onRowEditSave(item);

        // this has to be changed to a number in case IsProcessing === true below, if it is, the .toFixed() in the template will fail
        // b/c it tries to do a tofixed on the input value, which comes through as a string.  If isProcessing !== true, the result of the
        // saveItem is displayed, which comes through as a number.
        item.Ratepercent = Number(item.Ratepercent);
        item.Adjustedrate = Number(item.Adjustedrate);
        item.Status = {
            StatusCode: ResponseStatus.Loading,
            Message: 'Saving'
        };
        this.items = this.items.filter(x => x.id !== 'add');
        this.items = [{ ...this.addTemplate }, ...this.items];
        this.log.verbose({
            type: 'IndirectRateStructureComponent:onRowEditSave',
            item,
            cloned: this.cloned,
            selected: this.selected,
            userInfo: this.userInfo,
            items: this.items
        });

        this.SaveItem.next([item]);
        delete this.cloned[item.id];
        delete this.editing;
        this.messages = [
            {
                severity: 'info',
                summary: '',
                style: 'max-width: fit-content',
                detail: this.showAdjustments
                    ? 'Click Review FY Impact to review results prior to committing the FY indirect rate adjustment'
                    : 'Adjusted rate will be used for future processing, reprocess periods as necessary'
            } as any
        ];
    }
    onRowEditCancel(item: IndirectRateStructure) {
        super.onRowEditCancel(item);
        this.items = [{ ...this.addTemplate }, ...this.items];
    }
    onRowEditDelete(item: IndirectRateStructure) {
        this.confirmservice
            .confirmPromise({
                dialogText: `Are you sure you want to delete this rate?`
            })
            .then(() => {
                item.Status = {
                    StatusCode: ResponseStatus.Loading,
                    Message: 'Deleting'
                };
                this.items = [...this.items];

                this.DeleteItem.next([item]);

                delete this.cloned[item.id];
                delete this.editing;
                return;
            })
            .catch(e => {});
    }
}
